import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import {
  CameraOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Badge, Tooltip as AntTooltip } from "antd";
import axios from "../../utils/axiosDash";

// ─── Camera color palette (cycles) ───────────────────────────────────────────
const CAM_COLORS = [
  "#3b82f6","#8b5cf6","#10b981","#f59e0b","#ec4899",
  "#06b6d4","#f97316","#6366f1","#14b8a6","#ef4444",
  "#84cc16","#a855f7",
];
const camColor = (idx) => CAM_COLORS[idx % CAM_COLORS.length];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const todayIST = () => {
  const now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return now.toISOString().slice(0, 10);
};

const fmtDisplayDate = (dateStr) => {
  const [y, m, d] = dateStr.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
};

const isToday = (d) => d === todayIST();

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div className="flex flex-col items-center justify-center gap-4 py-32">
    <ReloadOutlined className="text-4xl text-blue-500 animate-spin" />
    <p className="text-slate-400 text-sm font-medium">Loading data…</p>
  </div>
);

// ─── Bar chart tooltip ────────────────────────────────────────────────────────
const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-xs">
      <p className="text-slate-400 font-mono mb-1">{label}</p>
      <p className="text-amber-600 font-bold text-sm">{payload[0].value} persons</p>
    </div>
  );
};

// ─── Mini sparkbar ────────────────────────────────────────────────────────────
const MiniBar = ({ hourly, peakHour, color }) => {
  const max    = Math.max(...hourly.map((h) => h.count), 1);
  const active = hourly.filter((h) => h.count > 0);
  const bars   = active.length ? active : hourly;
  return (
    <div className="flex items-end gap-0.5 h-10 w-full">
      {bars.map((h) => (
        <AntTooltip key={h.hour} title={`${h.label}: ${h.count} persons`}>
          <div
            className="flex-1 rounded-t-sm transition-opacity duration-200"
            style={{
              height: `${Math.max(4, (h.count / max) * 100)}%`,
              background: h.hour === peakHour ? "#d97706" : color,
              opacity: h.hour === peakHour ? 1 : 0.4,
            }}
          />
        </AntTooltip>
      ))}
    </div>
  );
};

// ─── Fleet stat card ──────────────────────────────────────────────────────────
const FleetStat = ({ label, value, icon, colorCls, bgCls, borderCls }) => (
  <div className={`${bgCls} ${borderCls} border rounded-2xl px-6 py-4 flex flex-col gap-1 min-w-[130px]`}>
    <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest ${colorCls}`}>
      {icon}
      {label}
    </div>
    <span className={`text-3xl font-black font-mono leading-none ${colorCls}`}>
      {value ?? "—"}
    </span>
  </div>
);

// ─── Date navigator ───────────────────────────────────────────────────────────
const DateNav = ({ selectedDate, onChange }) => {
  const today = todayIST();
  const isNow = selectedDate === today;

  const shiftDay = (delta) => {
    const d = new Date(selectedDate + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() + delta);
    const next = d.toISOString().slice(0, 10);
    if (next > today) return;
    onChange(next);
  };

  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
      <button
        onClick={() => shiftDay(-1)}
        className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
        title="Previous day"
      >
        <LeftOutlined className="text-xs" />
      </button>

      <input
        type="date"
        value={selectedDate}
        max={today}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className="bg-transparent border-none text-blue-600 font-mono text-sm font-bold outline-none cursor-pointer w-32"
        style={{ colorScheme: "light" }}
      />

      <button
        onClick={() => shiftDay(1)}
        disabled={isNow}
        className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors
          ${isNow ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-slate-100 hover:bg-slate-200 text-slate-600 cursor-pointer"}`}
        title="Next day"
      >
        <RightOutlined className="text-xs" />
      </button>

      {!isNow && (
        <button
          onClick={() => onChange(today)}
          className="text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1 text-[10px] font-bold tracking-widest hover:bg-amber-100 transition-colors"
        >
          TODAY
        </button>
      )}
    </div>
  );
};

// ─── Camera card ──────────────────────────────────────────────────────────────
const CameraCard = ({ cam, idx, onSelect, selected }) => {
  const color      = camColor(idx);
  const isSelected = selected?.camera_id === cam.camera_id;

  return (
    <div
      onClick={() => onSelect(isSelected ? null : cam)}
      className={`relative bg-white rounded-2xl p-5 cursor-pointer flex flex-col gap-3 overflow-hidden transition-all duration-200
        ${isSelected
          ? "ring-2 shadow-lg"
          : "border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
        }`}
      style={isSelected ? { borderColor: color, ringColor: color, boxShadow: `0 0 0 2px ${color}33, 0 4px 20px #0000000f` } : {}}
    >
      {/* Top stripe */}
      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: color }} />

      {/* Header */}
      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
            <CameraOutlined style={{ color, fontSize: 14 }} />
          </div>
          <span className="text-xs font-bold text-slate-700">
            <span className="font-mono tracking-wide">{cam.camera_id}</span>
            <span className="text-slate-400 mx-1">-</span>
            <span className="font-medium text-slate-600">{cam.camera_name}</span>
          </span>
        </div>

        {cam.alerts > 0 && (
          <span className="flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
            <WarningOutlined />
            {cam.alerts} alert{cam.alerts > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Count */}
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-black font-mono leading-none" style={{ color }}>
          {cam.total.toLocaleString()}
        </span>
        <span className="text-xs text-slate-400">persons</span>
      </div>

      {/* Peak */}
      <div className="flex items-center gap-2 text-xs">
        <span className="flex items-center gap-1 bg-amber-50 text-amber-600 border border-amber-200 rounded-md px-2 py-0.5 font-bold text-[10px]">
          <ThunderboltOutlined /> Peak
        </span>
        <span className="font-mono text-slate-600 font-semibold">{cam.peak_hour_label}</span>
        <span className="text-slate-400">— {cam.peak_count} persons</span>
      </div>

      {/* Sparkbar */}
      <MiniBar hourly={cam.hourly_breakdown} peakHour={cam.peak_hour} color={color} />

      <p className="text-[10px] text-slate-300 text-right">
        {isSelected ? "Click to collapse ↑" : "Click for full chart →"}
      </p>
    </div>
  );
};

// ─── Hourly drawer ────────────────────────────────────────────────────────────
const HourlyDrawer = ({ cam, idx, onClose }) => {
  const color = camColor(idx);
  return (
    <div
      className="bg-white rounded-2xl p-7 shadow-xl col-span-full"
      style={{ border: `1.5px solid ${color}` }}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-base font-bold text-slate-800 m-0">
            <span className="font-mono" style={{ color }}>{cam.camera_id}</span>
            {" "}— Hourly Breakdown
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Total:{" "}
            <strong className="text-slate-700">{cam.total}</strong>
            {" · "}Peak at{" "}
            <strong className="text-amber-600">{cam.peak_hour_label}</strong>
            {" "}({cam.peak_count} persons)
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
        >
          <CloseOutlined /> Close
        </button>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={cam.hourly_breakdown} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#94a3b8", fontFamily: "monospace" }}
            tickLine={false}
            axisLine={{ stroke: "#cbd5e1" }}
            interval={1}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <ReferenceLine
            x={cam.peak_hour_label}
            stroke="#d97706"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            label={{ value: "Peak", position: "top", fill: "#d97706", fontSize: 10, fontWeight: 700 }}
          />
          <Tooltip content={<BarTooltip />} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {cam.hourly_breakdown.map((h) => (
              <Cell
                key={h.hour}
                fill={h.hour === cam.peak_hour ? "#d97706" : color}
                opacity={h.count === 0 ? 0.12 : h.hour === cam.peak_hour ? 1 : 0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CameraPersonCountDashboard() {
  const [selectedDate,  setSelectedDate]  = useState(todayIST());
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [selectedCam,   setSelectedCam]   = useState(null);
  const [selectedIdx,   setSelectedIdx]   = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [countdown,     setCountdown]     = useState(60);
  const timerRef = useRef(null);
  const cdRef    = useRef(null);

  const fetchData = useCallback(async (date) => {
    try {
      setError(null);
      setLoading(true);
      const res = await axios.get(`/person-count-tanseeq?date=${date}`);
      
      // Extract data from response
      let responseData;
      if (res.data && res.data.success && res.data.data) {
        responseData = res.data.data;
      } else if (res.data && res.data.data) {
        responseData = res.data.data;
      } else {
        responseData = res.data;
      }
      
      setData(responseData);
      setLastRefreshed(new Date());
      setCountdown(60);
      setSelectedCam(null);
      setSelectedIdx(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Retrying…");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedDate);
    clearInterval(timerRef.current);
    clearInterval(cdRef.current);
    if (isToday(selectedDate)) {
      timerRef.current = setInterval(() => fetchData(selectedDate), 60_000);
      cdRef.current    = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1_000);
    }
    return () => {
      clearInterval(timerRef.current);
      clearInterval(cdRef.current);
    };
  }, [selectedDate, fetchData]);

  const handleSelect = (cam, idx) => {
    if (selectedCam?.camera_id === cam?.camera_id) {
      setSelectedCam(null); setSelectedIdx(null);
    } else {
      setSelectedCam(cam); setSelectedIdx(idx);
    }
  };

  const viewingToday = isToday(selectedDate);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .cam-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }
        @media (max-width: 640px) { .cam-grid { grid-template-columns: 1fr; } }
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-3">

          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-sm">
              <VideoCameraOutlined className="text-white text-base" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 leading-tight">Camera Analytics</p>
              <p className="text-[11px] text-slate-400 font-mono leading-tight">Daily Person Count</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            {lastRefreshed && (
              <span className="text-[11px] text-slate-400 font-mono hidden sm:block">
                <ClockCircleOutlined className="mr-1" />
                {lastRefreshed.toLocaleTimeString()}
              </span>
            )}

            <DateNav selectedDate={selectedDate} onChange={setSelectedDate} />

            {/* Countdown — today only */}
            {viewingToday && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-2xl px-3 py-1.5">
                <svg width="24" height="24" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="12" cy="12" r="9" fill="none" stroke="#bfdbfe" strokeWidth="2.5" />
                  <circle
                    cx="12" cy="12" r="9" fill="none" stroke="#3b82f6" strokeWidth="2.5"
                    strokeDasharray={2 * Math.PI * 9}
                    strokeDashoffset={2 * Math.PI * 9 * (1 - countdown / 60)}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 1s linear" }}
                  />
                </svg>
                <span className="text-xs font-bold text-blue-500 font-mono">{countdown}s</span>
              </div>
            )}

            {/* Live / Historical badge */}
            <Badge
              dot
              status={viewingToday ? "success" : "processing"}
              text={
                <span className={`text-xs font-bold tracking-wide ${viewingToday ? "text-emerald-600" : "text-cyan-600"}`}>
                  {viewingToday ? "LIVE" : "HISTORICAL"}
                </span>
              }
              className={`px-3 py-1.5 rounded-full border ${
                viewingToday
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-cyan-50 border-cyan-200"
              }`}
            />
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Date headline */}
        <div className="flex items-baseline gap-3 mb-8">
          <h1 className="text-2xl font-black text-slate-800 m-0">{fmtDisplayDate(selectedDate)}</h1>
          {!viewingToday && (
            <span className="text-xs font-semibold text-cyan-600 bg-cyan-50 border border-cyan-200 rounded-lg px-2 py-0.5 flex items-center gap-1">
              <HistoryOutlined /> Historical snapshot
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 rounded-xl px-4 py-3 text-sm mb-6">
            <WarningOutlined /> {error}
          </div>
        )}

        {loading ? (
          <Spinner />
        ) : !data?.cameras || data.cameras.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-96 gap-3">
            <span className="text-5xl">📭</span>
            <p className="text-slate-600 font-semibold text-sm">No camera data for {fmtDisplayDate(selectedDate)}</p>
            <p className="text-slate-400 text-xs">Try a different date using the picker above</p>
          </div>
        ) : (
          <>
            {/* ── Fleet summary ──────────────────────────────────────────── */}
            <div className="mb-8">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                Fleet Summary · {data.fleet.camera_count} Cameras
              </p>
              <div className="flex flex-wrap gap-3">
                <FleetStat
                  label="Cameras Online"
                  value={data.fleet.camera_count || 0}
                  icon={<CameraOutlined />}
                  colorCls="text-emerald-600"
                  bgCls="bg-emerald-50"
                  borderCls="border-emerald-200"
                />
                <FleetStat
                  label="Alerts"
                  value={data.fleet.total_alerts || 0}
                  icon={<WarningOutlined />}
                  colorCls={(data.fleet.total_alerts || 0) > 0 ? "text-rose-600" : "text-emerald-600"}
                  bgCls={(data.fleet.total_alerts || 0) > 0 ? "bg-rose-50" : "bg-emerald-50"}
                  borderCls={(data.fleet.total_alerts || 0) > 0 ? "border-rose-200" : "border-emerald-200"}
                />
                {data.fleet.busiest_camera && (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-widest text-amber-600 flex items-center gap-1">
                      <ThunderboltOutlined /> Busiest Camera
                    </span>
                    <span className="text-2xl font-black font-mono text-amber-600">
                      {data.fleet.busiest_camera}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 mb-7" />

            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
              Per-Camera Breakdown
            </p>

            {/* ── Camera grid ───────────────────────────────────────────── */}
            <div className="cam-grid">
              {data.cameras.map((cam, idx) => {
                const isSelected = selectedCam?.camera_id === cam.camera_id;
                return (
                  <React.Fragment key={cam.camera_id}>
                    <CameraCard
                      cam={cam}
                      idx={idx}
                      selected={selectedCam}
                      onSelect={(c) => handleSelect(c, idx)}
                    />
                    {isSelected && (
                      <HourlyDrawer
                        key={`drawer-${cam.camera_id}`}
                        cam={cam}
                        idx={selectedIdx}
                        onClose={() => { setSelectedCam(null); setSelectedIdx(null); }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}